/** @jsxImportSource @emotion/react */

import { VaultObjectViewerData } from "@/components/vault/viewer/VaultObjectViewerModal";
import useVaultStore from "@/stores/zustand/vault/useVaultStore";
import HENGE from "@henge-inc/global-types";
import HUI from "@henge-inc/ui/v1_2";
import { motion } from "motion/react";
import React, { RefObject, useRef } from "react";
import { css } from "@emotion/react";
import { MAX_TAGS } from "@/components/vault/vault-object-util";
import { useScreenStore } from "@/stores/useScreenStore";

const tagListVariants = {
  show: {
    opacity: 1,
    height: "auto",
    marginTop: 6,
  },
  hide: {
    opacity: 0,
    height: 0,
    marginTop: 0,
  },
} as const;

interface MetadataTitleProps {
  // name
  newNameInputRef: RefObject<HTMLInputElement>;
  newName: string;
  setNewName: React.Dispatch<React.SetStateAction<string>>;
  newNameInputErrorMessage: string;
  // tags
  tagListRef: RefObject<HTMLDivElement>;
  newTagValueList: string[];
  newTagInputRef: RefObject<HTMLInputElement>;
  inputTagValue: string;
  setInputTagValue: (value: string) => void;
  newTagInputErrorMessage: string;
  handleAddTag: () => void;
  handleRemoveTag: () => void;
  // data
  vaultObjectViewerData: VaultObjectViewerData | null;
}

export const MetadataTitle = ({
  newNameInputRef,
  newName,
  setNewName,
  newNameInputErrorMessage,
  tagListRef,
  newTagValueList,
  newTagInputRef,
  inputTagValue,
  setInputTagValue,
  newTagInputErrorMessage,
  handleAddTag,
  handleRemoveTag,
  vaultObjectViewerData,
}: MetadataTitleProps) => {
  const currentPage = useVaultStore((state) => state.currentPage);
  const textColor = useVaultStore((state) => state.vaultObjectViewerTextColor);
  const isMobileScreen = useScreenStore((state) => state.isMobileScreen);

  const isComposing = useRef(false);

  return (
    <motion.div
      key={"vault-object-viewer-metadata-title"}
      initial={{ opacity: 0, y: 0 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="flex flex-col justify-start items-start w-full h-full overflow-visible"
    >
      {currentPage === "uploader" ? (
        <div className={"relative flex items-center w-full px-1"}>
          {newNameInputErrorMessage && (
            <div
              className={"absolute -top-2 left-2 -translate-y-full"}
              css={css`
                & > div {
                  filter: drop-shadow(0px 0px 4px var(--gray50));
                }
              `}
            >
              <HUI.Popover
                arrowFace={HENGE.POSITION_FACE.bottom}
                arrowAlign={HENGE.FLEX_ALIGN.start}
                contentDirection={HENGE.FLEX_DIRECTION.row}
              >
                <HUI.Text
                  kind={HENGE.TEXT_KIND.small}
                  colour={HENGE.SEMANTIC_TEXT_COLOUR["text-primary"]}
                >
                  {newNameInputErrorMessage}
                </HUI.Text>
              </HUI.Popover>
            </div>
          )}
          <HUI.Input
            ref={newNameInputRef}
            kind={HENGE.INPUT_KIND.transparent}
            type="text"
            placeholder="Object Name"
            fullWidth
            maxLength={64}
            value={newName}
            setValue={setNewName}
            textStyleProps={{
              kind: HENGE.TEXT_KIND.heading2,
              weight: HENGE.TEXT_WEIGHT.semibold,
              colour:
                textColor === "#000000"
                  ? HENGE.PURE_COLOUR["text-black"]
                  : HENGE.PURE_COLOUR["text-white"],
              lineHeight: HENGE.TEXT_LINE_HEIGHT.small,
            }}
          />
        </div>
      ) : (
        <div
          className={"flex items-start w-full px-2 pt-1"}
          style={{
            width: "100%",
          }}
        >
          <HUI.Text
            kind={HENGE.TEXT_KIND.heading2}
            weight={HENGE.TEXT_WEIGHT.semibold}
            align={HENGE.TEXT_ALIGN.left}
            whiteSpace={HENGE.TEXT_WHITE_SPACE.normal}
            lineHeight={HENGE.TEXT_LINE_HEIGHT.small}
            colour={
              textColor === "#000000"
                ? HENGE.PURE_COLOUR["text-black"]
                : HENGE.PURE_COLOUR["text-white"]
            }
          >
            {vaultObjectViewerData?.name}
          </HUI.Text>
        </div>
      )}

      <motion.div
        ref={tagListRef}
        variants={tagListVariants}
        initial={undefined}
        animate={isMobileScreen ? tagListVariants.hide : tagListVariants.show}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className={
          "relative w-full h-7 flex flex-nowrap justify-start items-center gap-2 px-1 pt-1"
        }
      >
        {currentPage === "uploader" ? (
          <>
            {newTagValueList.map((tag, index) => (
              <div
                key={`${index}-${tag}`}
                className="px-2 rounded-lg h-7 flex items-center justify-center"
                style={{
                  backgroundColor: "var(--gray50-alpha40)",
                  color: textColor,
                }}
              >
                <HUI.Text
                  kind={HENGE.TEXT_KIND.body2}
                  weight={HENGE.TEXT_WEIGHT.medium}
                  colour={
                    textColor === "#000000"
                      ? HENGE.SEMANTIC_TEXT_COLOUR["text-tertiary"]
                      : HENGE.SEMANTIC_TEXT_COLOUR["text-mono-inverted"]
                  }
                  whiteSpace={HENGE.TEXT_WHITE_SPACE.nowrap}
                >
                  #{tag}
                </HUI.Text>
              </div>
            ))}
            <div className="flex items-center w-full h-7">
              {newTagInputErrorMessage && (
                <div
                  className={"absolute -top-2 left-2 -translate-y-full"}
                  css={css`
                    & > div {
                      filter: drop-shadow(0 0 4px var(--gray50));
                    }
                  `}
                >
                  <HUI.Popover
                    arrowFace={HENGE.POSITION_FACE.bottom}
                    arrowAlign={HENGE.FLEX_ALIGN.start}
                    contentDirection={HENGE.FLEX_DIRECTION.row}
                  >
                    <HUI.Text
                      kind={HENGE.TEXT_KIND.small}
                      colour={HENGE.SEMANTIC_TEXT_COLOUR["text-primary"]}
                    >
                      {newTagInputErrorMessage}
                    </HUI.Text>
                  </HUI.Popover>
                </div>
              )}
              <HUI.Input
                fullWidth
                ref={newTagInputRef}
                kind={HENGE.INPUT_KIND.transparent}
                type="text"
                size={HENGE.DETAILED_SIZE.small}
                sizeAttr={24}
                placeholder={
                  newTagValueList.length >= MAX_TAGS
                    ? "태그는 최대 5개까지 추가할 수 있어요."
                    : "태그를 추가해 보세요!"
                }
                maxLength={32}
                value={inputTagValue}
                setValue={(value) => {
                  if (newTagValueList.length >= MAX_TAGS) {
                    setInputTagValue("");
                    return;
                  }

                  setInputTagValue(value);
                }}
                textStyleProps={{
                  kind: HENGE.TEXT_KIND.body2,
                  weight: HENGE.TEXT_WEIGHT.regular,
                  colour:
                    textColor === "#000000"
                      ? HENGE.PURE_COLOUR["text-black"]
                      : HENGE.PURE_COLOUR["text-white"],
                }}
                placeholderColour={
                  textColor === "#000000"
                    ? HENGE.SEMANTIC_TEXT_COLOUR["text-tertiary"]
                    : HENGE.PURE_COLOUR["text-white"]
                }
                onCompositionStart={() => {
                  isComposing.current = true;
                }}
                onCompositionEnd={() => {
                  isComposing.current = false;
                }}
                onKeyDown={(e) => {
                  if (isComposing.current) return;

                  if (e.key === "Enter" || e.key === " " || e.key === "Tab") {
                    e.preventDefault();

                    handleAddTag();
                  } else if (
                    e.key === "Backspace" &&
                    newTagInputRef.current?.value === "" &&
                    newTagValueList.length > 0
                  ) {
                    e.preventDefault();
                    handleRemoveTag();
                  }
                }}
                onBlur={handleAddTag}
              />
            </div>
          </>
        ) : (
          <>
            {vaultObjectViewerData?.tagList.map((tag) => (
              <div
                key={tag.value}
                className="px-2 rounded-lg h-7 flex items-center justify-center mt-1"
                style={{
                  backgroundColor: "var(--gray50-alpha40)",
                  color: textColor,
                }}
              >
                <HUI.Text
                  kind={HENGE.TEXT_KIND.body2}
                  weight={HENGE.TEXT_WEIGHT.medium}
                  colour={
                    textColor === "#000000"
                      ? HENGE.SEMANTIC_TEXT_COLOUR["text-tertiary"]
                      : HENGE.SEMANTIC_TEXT_COLOUR["text-mono-inverted"]
                  }
                  whiteSpace={HENGE.TEXT_WHITE_SPACE.nowrap}
                >
                  #{tag.value}
                </HUI.Text>
              </div>
              // <HUI.Tag
              //   kind={"secondary"}
              //   key={tag.value}
              //   label={`#${tag.value}`}
              // />
            ))}
          </>
        )}
      </motion.div>
    </motion.div>
  );
};
